// @flow

import sortBy from 'lodash/sortBy';
import moment from 'moment';
import { geocodeByAddress, getLatLng } from '@clark-inc/clark-forms';

import {
  type SessionAttributesType,
  type SessionStatusType,
  type SessionRatingType,
} from 'types/sessions';
import combineDateAndTimeObject from 'utils/helpers/combine-date-time';

const buildInitialValues = ({
  startTime,
  endTime,
  sessionReportBody,
  sessionReportRating,
  internalNotes,
  locationRaw,
}: SessionAttributesType) => {
  const formatTime = t => moment(t).format('h:mm');
  const getPeriod = t => moment(t).format('a');
  const date = moment(startTime).startOf('day');
  const selectedWeekday = moment(date).isoWeekday();

  return {
    startDate: { date: moment(startTime).startOf('day') },
    startTime: { time: formatTime(startTime), period: getPeriod(startTime) },
    endTime: { time: formatTime(endTime), period: getPeriod(endTime) },
    selectedWeekday,
    sessionReportBody,
    sessionReportRating,
    internalNotes,
    locationRaw,
  };
};

const calculateStatus = (status: SessionStatusType, endDateTime: string) => {
  if (status === 'canceled') {
    return 'canceled';
  }
  if (moment(endDateTime).isAfter()) {
    return 'pending';
  }

  return 'confirmed';
};

export const convertIsoDayOfWeekToStartDate = (start: moment, selected: any) => {
  const prev = moment(start)
    .subtract(1, 'weeks')
    .isoWeekday(selected);
  const current = moment(start).isoWeekday(selected);
  const next = moment(start)
    .add(1, 'weeks')
    .isoWeekday(selected);

  const prevDiff = Math.abs(prev.diff(start, 'days'));
  const currentDiff = Math.abs(current.diff(start, 'days'));
  const nextDiff = next.diff(start, 'days');

  const mapWeekToDate = {
    [prevDiff]: prev,
    [currentDiff]: current,
    [nextDiff]: next,
  };

  const nearest = sortBy([prevDiff, currentDiff, nextDiff])[0];

  return mapWeekToDate[nearest];
};

type SubmitValuesType = {
  startDate: { date: moment },
  startTime: { time: string, period: string },
  endTime: { time: string, period: string },
  selectedWeekday?: number,
  sessionReportBody: ?string,
  sessionReportRating: ?SessionRatingType,
  internalNotes: ?string,
  locationRaw: ?string,
};

export const buildSubmitValues = async (
  status: SessionStatusType,
  shouldUpdateFutureSessions: boolean,
  {
    startDate: { date },
    startTime,
    endTime,
    selectedWeekday,
    sessionReportBody,
    sessionReportRating,
    internalNotes,
    locationRaw,
  }: SubmitValuesType,
) => {
  let updateFutureSessionsDate;
  let startDateTime;
  let endDateTime;
  if (shouldUpdateFutureSessions) {
    updateFutureSessionsDate = convertIsoDayOfWeekToStartDate(date, selectedWeekday);
    startDateTime = combineDateAndTimeObject(updateFutureSessionsDate, startTime);
    endDateTime = combineDateAndTimeObject(updateFutureSessionsDate, endTime);
  } else {
    startDateTime = combineDateAndTimeObject(date, startTime);
    endDateTime = combineDateAndTimeObject(date, endTime);
  }

  // set these to null so that they override previous values
  // if new location doesn't have corresponding lat/long
  let locationLatitude = null;
  let locationLongitude = null;
  try {
    const results = await geocodeByAddress(locationRaw);
    const { lat, lng } = await getLatLng(results[0]);
    locationLatitude = lat;
    locationLongitude = lng;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }

  return {
    startTime: startDateTime,
    endTime: endDateTime,
    shouldUpdateFutureSessions,
    sessionReportBody,
    sessionReportRating,
    internalNotes,
    status: calculateStatus(status, endDateTime),
    locationRaw,
    locationLatitude,
    locationLongitude,
  };
};

export default buildInitialValues;



// WEBPACK FOOTER //
// ./src/screens/sessions/edit/helpers.js